import React, { useEffect, useRef, useState } from "react";
import {
  AgoraRTCProvider,
  useJoin,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
} from "agora-rtc-react";
import AgoraRTC from "agora-rtc-sdk-ng";

// MUI Components
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  styled,
  useTheme,
  alpha,
  Button
} from "@mui/material";

// MUI Icons
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  CallEnd as CallEndIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Minimize as MinimizeIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  FiberManualRecord as FiberManualRecordIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  GraphicEq as GraphicEqIcon,
} from "@mui/icons-material";

// Styled Components
const DraggablePaper = styled(Paper)(({ theme }) => ({
  position: "fixed",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
  display: "flex",
  flexDirection: "column",
  zIndex: 1000,
  background: theme.palette.background.default,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
}));

const AudioContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  overflow: "hidden",
  background: theme.palette.background.default,
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ControlButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[800],
  color: "white",
  "&:hover": {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.grey[700],
  },
  width: 56,
  height: 56,
}));

const EndCallButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
  width: 56,
  height: 56,
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: 0,
  bottom: 0,
  width: 16,
  height: 16,
  cursor: "nwse-resize",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  "&::after": {
    content: '""',
    width: 8,
    height: 8,
    borderRight: `2px solid ${theme.palette.primary.main}`,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
    opacity: 0.75,
  },
}));

const MinimizedContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  right: 24,
  bottom: 24,
  zIndex: 1001,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: 28,
  background: theme.palette.background.paper,
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  cursor: "pointer",
}));

const VisualizerContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[900],
  borderRadius: 12,
  overflow: "hidden",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// 🎵 Visualizer Component
const AudioVisualizer = ({ track }) => {
  const canvasRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (!track) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const mediaStream = new MediaStream([track.getMediaStreamTrack()]);
    const source = audioContext.createMediaStreamSource(mediaStream);

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = theme.palette.grey[900];
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = dataArray[i] / 2;
        const hue = 180 + (i / bufferLength) * 150;
        ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
        ctx.fillRect(
          x,
          canvas.height - barHeight,
          barWidth,
          barHeight
        );
        x += barWidth + 1;
      }
    };

    draw();

    return () => {
      audioContext.close();
    };
  }, [track, theme]);

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={100}
      style={{ width: "100%", height: "80px", borderRadius: "8px" }}
    />
  );
};

// ---------------- Modern Audio Call Modal ----------------
function ModernAudioCallModal({
  isOpen,
  onClose,
  children,
  title = "Audio Call",
  // Button state and handlers
  micMuted,
  onToggleMic,
  onEndCall,
  onAddParticipants,
}) {
  const theme = useTheme();
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState({ x: 80, y: 60 });
  const [size, setSize] = useState({ w: 500, h: 600 });
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Drag handlers
  const onMouseDownHeader = (e) => {
    if (minimized) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setPos({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const onMouseUp = () => {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  // Resize handle
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const onMouseDownResize = (e) => {
    if (minimized) return;
    resizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", onResizeUp);
    e.stopPropagation();
  };

  const onResizeMove = (e) => {
    if (!resizing.current) return;
    const dw = e.clientX - resizeStart.current.x;
    const dh = e.clientY - resizeStart.current.y;
    const newW = Math.max(400, resizeStart.current.w + dw);
    const newH = Math.max(500, resizeStart.current.h + dh);
    setSize({ w: newW, h: newH });
  };

  const onResizeUp = () => {
    resizing.current = false;
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", onResizeUp);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {!minimized && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 999,
          }}
        />
      )}

      {/* Dock when minimized */}
      {minimized && (
        <MinimizedContainer onClick={() => setMinimized(false)}>
          <FiberManualRecordIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
          <Typography variant="body2" color="text.primary">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click to restore
          </Typography>
        </MinimizedContainer>
      )}

      {/* Modal */}
      {!minimized && (
        <DraggablePaper
          ref={modalRef}
          sx={{
            left: pos.x,
            top: pos.y,
            width: size.w,
            height: size.h,
          }}
        >
          {/* Header */}
          <Header onMouseDown={onMouseDownHeader}>
            <DragIndicatorIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <FiberManualRecordIcon sx={{ color: theme.palette.primary.main, fontSize: 12, mr: 1 }} />
              <Typography variant="subtitle1" color="text.primary">
                {title}
              </Typography>
            </Box>

            {/* Window controls */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Minimize">
                <IconButton size="small" onClick={() => setMinimized(true)}>
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Header>

          {/* Content */}
          <AudioContainer>
            {children}
          </AudioContainer>

          {/* Controls */}
          <ControlsContainer>
            <Tooltip title={micMuted ? "Unmute microphone" : "Mute microphone"}>
              <ControlButton
                active={!micMuted}
                onClick={onToggleMic}
              >
                {micMuted ? <MicOffIcon /> : <MicIcon />}
              </ControlButton>
            </Tooltip>

            <Tooltip title="Add Participants">
              <ControlButton onClick={() => setParticipantsOpen(true)}>
                <AddIcon />
              </ControlButton>
            </Tooltip>

            <Tooltip title="Settings">
              <ControlButton>
                <SettingsIcon />
              </ControlButton>
            </Tooltip>

            <Tooltip title="End Call">
              <EndCallButton onClick={onEndCall}>
                <CallEndIcon />
              </EndCallButton>
            </Tooltip>
          </ControlsContainer>

          {/* Resize handle */}
          <ResizeHandle onMouseDown={onMouseDownResize} />

          {/* Participants Dialog */}
          <Dialog open={participantsOpen} onClose={() => setParticipantsOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ mr: 1 }} />
                Participants
              </Box>
            </DialogTitle>
            <DialogContent>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>Y</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="You" secondary="Host" />
                  <Chip
                    icon={micMuted ? <MicOffIcon /> : <MicIcon />}
                    label={micMuted ? "Muted" : "Unmuted"}
                    size="small"
                    color={micMuted ? "default" : "primary"}
                    variant="outlined"
                  />
                </ListItem>
                <ListItem secondaryAction={
                  <IconButton edge="end">
                    <MoreVertIcon />
                  </IconButton>
                }>
                  <ListItemAvatar>
                    <Avatar>J</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="John Doe" />
                  <Chip
                    icon={<MicIcon />}
                    label="Unmuted"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </ListItem>
                <ListItem secondaryAction={
                  <IconButton edge="end">
                    <MoreVertIcon />
                  </IconButton>
                }>
                  <ListItemAvatar>
                    <Avatar>J</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="Jane Smith" />
                  <Chip
                    icon={<MicOffIcon />}
                    label="Muted"
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </ListItem>
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setParticipantsOpen(false)}>Close</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={onAddParticipants}>
                Add Participant
              </Button>
            </DialogActions>
          </Dialog>
        </DraggablePaper>
      )}
    </>
  );
}

// 🎙️ Main Room Component
const AudioRoom = ({ client, APP_ID, TOKEN, CHANNEL, uid }) => {
  const remoteUsers = useRemoteUsers();
  const [micMuted, setMicMuted] = useState(false);
  const [open, setOpen] = useState(true);
  const [localCameraTrack, setLocalCameraTrack] = useState(null);
  const [localMicrophoneTrack, setLocalMicrophoneTrack] = useState(null);
  const [joined, setJoined] = useState(false);
  const [role, setRole] = useState("host");
  const [cameraOff, setCameraOff] = useState(false);

  console.log(client);
  console.log("Remote users:", remoteUsers);


  useEffect(() => {
    remoteUsers.forEach(user => {
      if (user.audioTrack && !user._audioPlayed) {
        user.audioTrack.play();
        user._audioPlayed = true; // mark so we don’t re-play
      }
    });
  }, [remoteUsers]);


  useEffect(() => {
    if (!client) return;
  
    const handleUserPublished = async (user, mediaType) => {
      console.log("User published:", user.uid, mediaType);
      await client.subscribe(user, mediaType);
      if (mediaType === "audio") {
        user.audioTrack.play(); // play immediately
      }
    };
  
    const handleUserUnpublished = (user, mediaType) => {
      console.log("User unpublished:", user.uid, mediaType);
    };
  
    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);
  
    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
    };
  }, [client]);
  
  


  async function publishWithRetry(client, tracks, maxRetries = 3, delay = 2000) {
    const validTracks = tracks.filter(t => t && t.trackMediaType);
    if (validTracks.length === 0) {
      throw new Error("❌ No valid local tracks to publish.");
    }

    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        console.log(`Publishing attempt ${attempt + 1}`);
        await client.publish(tracks);
        console.log("✅ Published successfully");
        return true;
      } catch (err) {
        console.warn("❌ Publish failed:", err);
        attempt++;
        if (attempt < maxRetries) {
          await new Promise(res => setTimeout(res, delay));
        } else {
          throw new Error("Max retries reached. Could not publish tracks.");
        }
      }
    }
  }



  // join + publish local mic
  // useJoin({ appid: APP_ID, channel: CHANNEL, token: TOKEN, uid: uid });

  const { data, isConnected, isLoading, error } = useJoin({
    appid: APP_ID,
    channel: CHANNEL,
    token: TOKEN,
    uid: uid
  });


  // usePublish([localMicrophoneTrack]);


  useEffect(() => {
    return () => {
      if (client) {
        client.leave().catch(err => console.error("Leave failed:", err));
      }
    };
  }, [client]);


  useEffect(() => {
    if (!isConnected) return;
  
    let micTrack;
  
    const initLocalTracks = async () => {
      try {
        micTrack = await AgoraRTC.createMicrophoneAudioTrack();
        setLocalMicrophoneTrack(micTrack);
        await publishWithRetry(client, [micTrack]);
      } catch (err) {
        console.error("Failed to init local tracks:", err);
      }
    };
    initLocalTracks();
  
    return () => {
      if (micTrack) {
        micTrack.stop();
        micTrack.close();
        client.unpublish([micTrack]).catch(() => {});
      }
    };
  }, [isLoading]);
  


  const toggleMic = () => {
    if (localMicrophoneTrack) {
      if (micMuted) {
        localMicrophoneTrack.setEnabled(true);
      } else {
        localMicrophoneTrack.setEnabled(false);
      }
      setMicMuted(!micMuted);
    }
  };

  const endCall = async () => {
    if (localMicrophoneTrack) {
      localMicrophoneTrack.stop();
      localMicrophoneTrack.close();
      await client.unpublish([localMicrophoneTrack]).catch(() => {});
    }
    await client.leave().catch(() => {});
    setOpen(false);
  };
  

  const addParticipants = () => {
    // In a real app, this would open a dialog to invite participants
    alert("This would open a dialog to add participants in a real application");
  };

  return (
    <ModernAudioCallModal
      isOpen={open}
      onClose={() => setOpen(false)}
      title="Audio Call"
      micMuted={micMuted}
      onToggleMic={toggleMic}
      onEndCall={endCall}
      onAddParticipants={addParticipants}
    >
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <GraphicEqIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          Audio Call
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {CHANNEL}
        </Typography>
      </Box>

      {/* Local User */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: "primary.main" }}>Y</Avatar>
          You {micMuted && <Chip icon={<MicOffIcon />} label="Muted" size="small" color="error" sx={{ ml: 1 }} />}
        </Typography>
        <VisualizerContainer>
          {localMicrophoneTrack ? (
            <AudioVisualizer track={localMicrophoneTrack} />
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography color="text.secondary">Initializing microphone...</Typography>
            </Box>
          )}
        </VisualizerContainer>
      </Box>

      {/* Remote Users */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Participants ({remoteUsers.length + 1})
        </Typography>

        {remoteUsers.length === 0 && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <PeopleIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography color="text.secondary">No one else has joined yet...</Typography>
          </Box>
        )}

        {remoteUsers.map((user) => (
          <Box key={user.uid} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: "secondary.main" }}>
                {String.fromCharCode(65 + (user.uid % 26))}
              </Avatar>
              User {user.uid}
            </Typography>
            <VisualizerContainer>
              {user.audioTrack ? (
                <AudioVisualizer track={user.audioTrack} />
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography color="text.secondary">No audio...</Typography>
                </Box>
              )}
            </VisualizerContainer>
          </Box>
        ))}

      </Box>
    </ModernAudioCallModal>
  );
};

// ✅ Wrapper with Agora Client
const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

export default function AudioCall(props) {
  return (
    <AgoraRTCProvider client={client}>
      <AudioRoom client={client} APP_ID={props.appId} TOKEN={props.token} CHANNEL={props.channel} uid={props.uid} />
    </AgoraRTCProvider>
  );
}